import { Box, Button, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { type ReactElement, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { NEUTRAL, SURFACE } from "../../theme/tokens";
import type { ShiftRequest } from "../../utils/models";
import { FillShiftModal } from "./NotificationsHistory/FillShiftModal";
import { ShiftDataContent } from "./ShiftDataContent";
import { ShiftDataHeader } from "./ShiftDataHeader";

enum DisplayType {
	Accepted = "Accepted Staff",
	Actions = "Actions",
}

export const RequestsPanel = () => {
	const theme = useTheme();
	const { id } = useParams();
	const { staffRequests } = useRequestsContext();
	const [displayType, setDisplayType] = useState<DisplayType>(DisplayType.Accepted);

	const request: ShiftRequest | undefined = useMemo(() => staffRequests.find((request) => request.id === id), [id, staffRequests]);

	const activeView = useMemo((): ReactElement | null => {
		if (!request) return null;

		switch (displayType) {
			case DisplayType.Accepted:
				return <ShiftDataContent request={request} />;
			case DisplayType.Actions:
				return (
					<FillShiftModal
						open={true}
						onClose={() => {}}
						requestId={request.id}
						shift_start={request.message.shift_start}
						shift_end={request.message.shift_end}
					/>
				);
		}
	}, [displayType, request]);

	return (
		<Box
			data-testid="request-panel-root"
			sx={{
				flexGrow: 1,
				height: "calc(100vh - 50px)",
				overflow: "auto",
				bgcolor: NEUTRAL[50],
				position: "absolute",
				top: "50px",
				left: 0,
				right: 0,
				bottom: 0,
				p: 0,
				m: 0,
				scrollBehavior: "smooth",
				overscrollBehavior: "contain",
			}}
		>
			{request && (
				<Container
					maxWidth="lg"
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column",
						p: 0,
						m: 0,
						overflowX: "hidden",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 1.5,
							width: "100%",
							mx: "auto",
							pb: 10,
						}}
					>
						<Box
							sx={{
								position: "sticky",
								top: 0,
								width: "100%",
								zIndex: 5,
								bgcolor: NEUTRAL[50],
							}}
						>
							<ShiftDataHeader request={request} />
						</Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-evenly",
							}}
						>
							{Object.values(DisplayType).map((contentMode: DisplayType) => (
								<Button
									key={contentMode}
									onClick={() => setDisplayType(contentMode)}
									sx={{
										color: displayType === contentMode ? SURFACE : theme.palette.primary.main,
										backgroundColor: displayType === contentMode ? theme.palette.primary.main : "transparent",
										borderRadius: "100px",
										height: "25px",
										transition: "all 0.3s",
										"&:hover": {
											backgroundColor: displayType === contentMode ? theme.palette.primary.dark : NEUTRAL[100],
										},
									}}
								>
									{contentMode}
								</Button>
							))}
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
							<Box
								sx={{
									width: "90%",
								}}
							>
								{activeView}
							</Box>
						</Box>
					</Box>
				</Container>
			)}
		</Box>
	);
};
